import { Component } from '@angular/core';
import {
  ApiInvoice,
  DesktopInvoice,
  InvoicesService,
} from 'src/app/services/invoices.service';
import {
  DesCreditPurchase,
  DesCreditPurchasesService,
} from 'src/app/services/des-credit-purchases.service';

type ReceiptType = 'desktop' | 'api' | 'des';

type CompletedInvoice = {
  type: ReceiptType;
  invoice: DesktopInvoice | ApiInvoice | DesCreditPurchase;
};

@Component({
  selector: 'app-receipts-page',
  templateUrl: './receipts-page.component.html',
  styleUrls: ['./receipts-page.component.scss'],
})
export class ReceiptsPageComponent {
  loadingInvoices = false;
  loadingApiInvoices = false;
  loadingDesPurchases = false;
  completedDesktopInvoices: DesktopInvoice[] = [];
  completedApiInvoices: ApiInvoice[] = [];
  completedDesPurchases: DesCreditPurchase[] = [];
  completedInvoices: CompletedInvoice[] = [];
  errorFetchingInvoices = '';
  errorFetchingApiInvoices = '';
  errorFetchingDesPurchases = '';

  constructor(
    private invoicesService: InvoicesService,
    private desCreditPurchasesService: DesCreditPurchasesService
  ) {
    this.getInvoices();
    this.getApiInvoices();
    this.getDesPurchases();
  }

  get loading() {
    return (
      this.loadingInvoices ||
      this.loadingApiInvoices ||
      this.loadingDesPurchases
    );
  }

  get hasMultipleTypes() {
    const types = new Set(this.completedInvoices.map((invoice) => invoice.type));
    return types.size > 1;
  }

  get hasApiInvoices() {
    return this.completedApiInvoices.length > 0;
  }

  typeLabel(type: ReceiptType) {
    if (type === 'desktop') {
      return 'Desktop';
    }
    if (type === 'api') {
      return 'API';
    }
    return 'DES Credits';
  }

  getInvoices() {
    this.loadingInvoices = true;
    this.invoicesService.getInvoicesForCurrentTenant().subscribe({
      next: (res) => {
        console.log('invoices for current tenant', res);
        this.loadingInvoices = false;
        this.completedDesktopInvoices = res.desktop_invoices.complete.map(
          (invoice) => invoice.desktop_invoice
        );
        this.sortInvoices();
      },
      error: (err) => {
        this.loadingInvoices = false;
        console.error(err);
        this.errorFetchingInvoices = err;
      },
    });
  }

  getApiInvoices() {
    this.loadingApiInvoices = true;
    this.invoicesService.getApiInvoicesForCurrentTenant().subscribe({
      next: (res) => {
        console.log('api invoices for current tenant', res);
        this.loadingApiInvoices = false;
        this.completedApiInvoices = res.api_invoices.complete;
        this.sortInvoices();
      },
      error: (err) => {
        this.loadingApiInvoices = false;
        console.error(err);
        this.errorFetchingApiInvoices = err;
      },
    });
  }

  getDesPurchases() {
    this.loadingDesPurchases = true;
    this.desCreditPurchasesService.getForCurrentTenant().subscribe({
      next: (res) => {
        this.loadingDesPurchases = false;
        this.completedDesPurchases = res.complete;
        this.sortInvoices();
      },
      error: (err) => {
        this.loadingDesPurchases = false;
        console.error(err);
        this.errorFetchingDesPurchases = err;
      },
    });
  }

  getDisplayTransactionMonth(completedInvoice: CompletedInvoice) {
    if (completedInvoice.type !== 'api') {
      return undefined;
    }
    return this.invoicesService.getDisplayTransactionMonth(
      (completedInvoice.invoice as ApiInvoice).transaction_yyyymm!
    );
  }

  sortInvoices() {
    if (this.loading) {
      return;
    }

    this.completedInvoices = [
      ...this.completedDesktopInvoices.map((invoice) => ({
        type: 'desktop' as ReceiptType,
        invoice,
      })),
      ...this.completedApiInvoices.map((invoice) => ({
        type: 'api' as ReceiptType,
        invoice,
      })),
      ...this.completedDesPurchases.map((invoice) => ({
        type: 'des' as ReceiptType,
        invoice,
      })),
    ];

    this.completedInvoices.sort((a, b) => {
      if (a.invoice.completed_at && b.invoice.completed_at) {
        const dateA = new Date(a.invoice.completed_at);
        const dateB = new Date(b.invoice.completed_at);
        return dateB.getTime() - dateA.getTime();
      }

      const dateA = new Date(a.invoice.created_at);
      const dateB = new Date(b.invoice.created_at);
      return dateB.getTime() - dateA.getTime();
    });
  }

  navigateToReceipt(invoice: CompletedInvoice) {
    if (invoice.type === 'desktop') {
      window.location.href = '/receipt/' + invoice.invoice.id;
    } else if (invoice.type === 'api') {
      window.location.href = '/api-receipt/' + invoice.invoice.id;
    } else {
      window.location.href = '/des-receipt/' + invoice.invoice.id;
    }
  }
}
